import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./CommissionReportPage.css";
import {
  ACCOUNT_STATUS_OPTIONS,
  COLORS,
  Chip,
  DonutChart,
  BarList,
  ChartLegend,
  PaginationBar,
  asNum,
  formatAmount,
  formatDate,
  personName,
  kycLabel,
  kycClass,
  accountClass,
  dateToFromIso,
  dateToToIso,
  packageOptionLabel,
} from "./commissionReportShared";

const EMPTY_PAGINATION = { page: 1, limit: 20, total: 0, total_pages: 1, has_next: false, has_prev: false };

const pkgId = (pkg) => pkg?._id || pkg?.id || "";

const CommissionReportPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [packages, setPackages] = useState([]);

  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [accountStatus, setAccountStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [commissionType, setCommissionType] = useState("");
  const [packageId, setPackageId] = useState("");
  const [referralLevel, setReferralLevel] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [exportLoading, setExportLoading] = useState(false);
  const [exportToast, setExportToast] = useState(null);

  const buildParams = useCallback(
    (page, limit, searchOverride) => {
      const q = typeof searchOverride === "string" ? searchOverride : search;
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (q.trim()) params.search = q.trim();
      if (accountStatus) params.account_status = accountStatus;
      const fromIso = dateToFromIso(fromDate);
      const toIso = dateToToIso(toDate);
      if (fromIso) params.from = fromIso;
      if (toIso) params.to = toIso;
      if (commissionType) params.commission_type = commissionType;
      if (packageId) params.package = packageId;
      if (referralLevel) params.referral_level = referralLevel;
      return params;
    },
    [search, accountStatus, fromDate, toDate, commissionType, packageId, referralLevel, sortBy, sortOrder]
  );

  const fetchRows = useCallback(
    async (page = 1, limit = pagination.limit, searchOverride) => {
      setLoading(true);
      setError("");
      try {
        const res = await AdminService.getUsersCommissionReport(
          buildParams(page, limit, searchOverride)
        );
        if (res?.success) {
          setRows(Array.isArray(res?.data) ? res.data : []);
          setPagination((prev) => ({
            ...prev,
            ...(res?.pagination || {}),
            page: res?.pagination?.page || page,
            limit: res?.pagination?.limit ?? limit,
          }));
        } else {
          setRows([]);
          setError(res?.message || "Failed to fetch commission report.");
        }
      } catch (err) {
        setRows([]);
        setError(err?.response?.data?.message || err?.message || "Failed to fetch commission report.");
      } finally {
        setLoading(false);
      }
    },
    [buildParams, pagination.limit]
  );

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load
  }, []);

  useEffect(() => {
    AdminService.getEarningPackages({ page: 1, limit: 100, status: "all" })
      .then((res) => {
        setPackages(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => setPackages([]));
  }, []);

  const charts = useMemo(() => {
    const totals = {
      stakingEarned: 0,
      brokerageEarned: 0,
      earned: 0,
      stakingGenerated: 0,
      brokerageGenerated: 0,
      generated: 0,
      stakingCount: 0,
      brokerageCount: 0,
    };
    const topEarned = [];
    const topGenerated = [];

    rows.forEach((row) => {
      const s = row?.commission_summary || {};
      totals.stakingEarned += asNum(s.staking_commission_earned);
      totals.brokerageEarned += asNum(s.brokerage_commission_earned);
      totals.earned += asNum(s.total_commission_earned);
      totals.stakingGenerated += asNum(s.staking_commission_generated_for_uplines);
      totals.brokerageGenerated += asNum(s.brokerage_commission_generated_for_uplines);
      totals.generated += asNum(s.total_commission_generated_for_uplines);
      totals.stakingCount += asNum(s.staking_commission_count);
      totals.brokerageCount += asNum(s.brokerage_commission_count);

      const name = personName(row);
      topEarned.push({ label: name, value: asNum(s.total_commission_earned), color: COLORS.earned });
      topGenerated.push({
        label: name,
        value: asNum(s.total_commission_generated_for_uplines),
        color: COLORS.generated,
      });
    });

    topEarned.sort((a, b) => b.value - a.value);
    topGenerated.sort((a, b) => b.value - a.value);

    return {
      totals,
      topEarned: topEarned.slice(0, 6),
      topGenerated: topGenerated.slice(0, 6),
      sourceSlices: [
        { label: "Staking earned", value: totals.stakingEarned, color: COLORS.staking },
        { label: "Trading earned", value: totals.brokerageEarned, color: COLORS.brokerage },
      ],
      flowSlices: [
        { label: "Earned (from downline)", value: totals.earned, color: COLORS.earned },
        { label: "Generated for uplines", value: totals.generated, color: COLORS.generated },
      ],
    };
  }, [rows]);

  const handleApply = (e) => {
    e.preventDefault();
    const q = searchDraft.trim();
    setSearch(q);
    fetchRows(1, pagination.limit, q);
  };

  const handleReset = () => {
    setSearch("");
    setSearchDraft("");
    setAccountStatus("");
    setFromDate("");
    setToDate("");
    setCommissionType("");
    setPackageId("");
    setReferralLevel("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setLoading(true);
    setError("");
    AdminService.getUsersCommissionReport({
      page: 1,
      limit: pagination.limit,
      sort_by: "createdAt",
      sort_order: "desc",
    })
      .then((res) => {
        if (res?.success) {
          setRows(Array.isArray(res?.data) ? res.data : []);
          setPagination((prev) => ({
            ...prev,
            ...(res?.pagination || {}),
            page: res?.pagination?.page || 1,
            limit: res?.pagination?.limit ?? pagination.limit,
          }));
        } else {
          setRows([]);
          setError(res?.message || "Failed to fetch commission report.");
        }
      })
      .catch((err) => {
        setRows([]);
        setError(err?.response?.data?.message || err?.message || "Failed to fetch commission report.");
      })
      .finally(() => setLoading(false));
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setExportToast(null);
    try {
      let currentPage = 1;
      let lastPage = 1;
      const all = [];
      do {
        const res = await AdminService.getUsersCommissionReport(buildParams(currentPage, 100));
        if (res?.success === false && !Array.isArray(res?.data)) {
          throw new Error(res?.message || "Failed to export.");
        }
        all.push(...(Array.isArray(res?.data) ? res.data : []));
        lastPage = res?.pagination?.total_pages || 1;
        currentPage += 1;
      } while (currentPage <= lastPage);

      if (!all.length) {
        setExportToast({ type: "error", message: "No data to export." });
        setTimeout(() => setExportToast(null), 3000);
        return;
      }

      const exportData = all.map((row) => {
        const s = row?.commission_summary || {};
        return {
          UID: row.uuid ?? "",
          Name: personName(row) === "—" ? "" : personName(row),
          Email: row.email ?? "",
          Mobile: row.mobile ?? "",
          Status: row.account_status ?? "",
          KYC: kycLabel(row.kyc_verified),
          "Direct referrals": row.total_direct_referrals ?? 0,
          "Registered": row.registration_date ?? "",
          "Staking earned": s.staking_commission_earned ?? 0,
          "Staking credits": s.staking_commission_count ?? 0,
          "Trading earned": s.brokerage_commission_earned ?? 0,
          "Trading credits": s.brokerage_commission_count ?? 0,
          "Total earned": s.total_commission_earned ?? 0,
          "Staking generated for uplines": s.staking_commission_generated_for_uplines ?? 0,
          "Trading generated for uplines": s.brokerage_commission_generated_for_uplines ?? 0,
          "Total generated for uplines": s.total_commission_generated_for_uplines ?? 0,
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Commission Report");
      XLSX.writeFile(workbook, `commission_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setExportToast({ type: "success", message: `Exported ${all.length} row(s) to Excel.` });
      setTimeout(() => setExportToast(null), 4000);
    } catch (err) {
      setExportToast({ type: "error", message: err?.message || "Failed to export Excel file." });
      setTimeout(() => setExportToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage cr-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Commission Report</span>
          </p>
          <h1 className="dashboard-hero__title">Commission Report</h1>
          <p className="dashboard-hero__subtitle">
            Staking and trading commission only — who earned it, who generated it, and how much went to uplines.
          </p>
        </div>
        <div className="dashboard-hero__icon cr-hero__icon">
          <i className="fas fa-chart-pie" />
        </div>
      </section>

      <div className="dashboard-body dashboard-content-inner">
        <p className="cr-help">
          Each row is one user. <strong>Earned</strong> is commission they received from their downline.
          <strong> Generated for uplines</strong> is commission their own staking and trades paid upward.
          Signup / referral bonus is not included in this report.
        </p>

        <div className="cr-stats">
          <div className="cr-stat">
            <span>Users</span>
            <strong>{pagination.total ?? 0}</strong>
            <small>Matching current filters</small>
          </div>
          <div className="cr-stat">
            <span>Earned on this page</span>
            <strong>{formatAmount(charts.totals.earned)}</strong>
            <small>
              {charts.totals.stakingCount + charts.totals.brokerageCount} credits
            </small>
          </div>
          <div className="cr-stat">
            <span>Staking vs trading earned</span>
            <strong>{formatAmount(charts.totals.stakingEarned)}</strong>
            <small>Trading {formatAmount(charts.totals.brokerageEarned)}</small>
          </div>
          <div className="cr-stat">
            <span>Generated for uplines (page)</span>
            <strong>{formatAmount(charts.totals.generated)}</strong>
            <small>Paid from these users’ own activity</small>
          </div>
        </div>

        <div className="cr-charts">
          <article className="cr-chart-card">
            <h3>Where earned commission came from</h3>
            <div className="cr-chart-card__body">
              <DonutChart
                slices={charts.sourceSlices}
                centerLabel={formatAmount(charts.totals.earned)}
                centerSub="This page"
              />
              <ChartLegend items={charts.sourceSlices} />
            </div>
          </article>
          <article className="cr-chart-card">
            <h3>Earned vs paid to uplines</h3>
            <div className="cr-chart-card__body">
              <DonutChart
                slices={charts.flowSlices}
                centerLabel={formatAmount(charts.totals.earned + charts.totals.generated)}
                centerSub="Both directions"
              />
              <ChartLegend items={charts.flowSlices} />
            </div>
          </article>
          <article className="cr-chart-card">
            <h3>Top earners on this page</h3>
            <BarList items={charts.topEarned} />
          </article>
        </div>

        <section className="cr-card">
          <form className="cr-toolbar" onSubmit={handleApply}>
            <input
              type="search"
              placeholder="Search name, email, mobile, UID…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              aria-label="Search users"
            />
            <label>
              Status
              <select value={accountStatus} onChange={(e) => setAccountStatus(e.target.value)}>
                <option value="">All</option>
                {ACCOUNT_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Type
              <select value={commissionType} onChange={(e) => setCommissionType(e.target.value)}>
                <option value="">Staking + trading</option>
                <option value="staking">Staking only</option>
                <option value="brokerage">Trading only</option>
              </select>
            </label>
            <label>
              Level
              <select value={referralLevel} onChange={(e) => setReferralLevel(e.target.value)}>
                <option value="">All levels</option>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={String(n)}>
                    Level {n}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Package
              <select value={packageId} onChange={(e) => setPackageId(e.target.value)}>
                <option value="">All packages</option>
                {packages.map((pkg) => (
                  <option key={pkgId(pkg)} value={pkgId(pkg)}>
                    {packageOptionLabel(pkg)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              From
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
            <label>
              Sort
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="createdAt">Registration date</option>
                <option value="total_refer">Direct referrals</option>
              </select>
            </label>
            <label>
              Order
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="desc">Newest / highest</option>
                <option value="asc">Oldest / lowest</option>
              </select>
            </label>
            <button type="submit" className="cr-btn-primary" disabled={loading}>
              Apply
            </button>
            <button type="button" className="cr-btn-ghost" onClick={handleReset} disabled={loading}>
              Reset
            </button>
            <button
              type="button"
              className="cr-export"
              onClick={handleExportExcel}
              disabled={exportLoading || loading}
            >
              <i className="fas fa-file-excel" />
              {exportLoading ? "Exporting…" : "Export Excel"}
            </button>
          </form>

          {exportToast ? (
            <p className={`cr-toast cr-toast--${exportToast.type}`}>{exportToast.message}</p>
          ) : null}

          {error ? (
            <div className="cr-empty">
              <i className="fas fa-exclamation-circle" />
              <p>{error}</p>
            </div>
          ) : loading ? (
            <div className="cr-empty">
              <i className="fas fa-spinner fa-spin" />
              <p>Loading commission report…</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="cr-empty">
              <i className="fas fa-inbox" />
              <p>No users match these filters.</p>
            </div>
          ) : (
            <>
              <div className="cr-table-wrap">
                <table className="cr-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Status</th>
                      <th>Direct refs</th>
                      <th>Staking earned</th>
                      <th>Trading earned</th>
                      <th>Total earned</th>
                      <th>Generated for uplines</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const s = row?.commission_summary || {};
                      return (
                        <tr key={row._id}>
                          <td>
                            <div className="cr-user-mini">
                              <strong>{personName(row)}</strong>
                              <span>{row.email || "—"}</span>
                              <span>
                                {row.uuid || "—"}
                                {row.mobile ? ` · ${row.mobile}` : ""}
                              </span>
                            </div>
                          </td>
                          <td>
                            <Chip kind={accountClass(row.account_status)}>{row.account_status || "—"}</Chip>
                            <div style={{ marginTop: "0.3rem" }}>
                              <Chip kind={kycClass(row.kyc_verified)}>{kycLabel(row.kyc_verified)}</Chip>
                            </div>
                            <div className="cr-muted" style={{ marginTop: "0.25rem", fontSize: "0.75rem" }}>
                              {formatDate(row.registration_date)}
                            </div>
                          </td>
                          <td className="cr-num">{row.total_direct_referrals ?? 0}</td>
                          <td className="cr-num">
                            {formatAmount(s.staking_commission_earned)}
                            <div className="cr-muted">{s.staking_commission_count ?? 0} credits</div>
                          </td>
                          <td className="cr-num">
                            {formatAmount(s.brokerage_commission_earned)}
                            <div className="cr-muted">{s.brokerage_commission_count ?? 0} credits</div>
                          </td>
                          <td className="cr-num">
                            <strong>{formatAmount(s.total_commission_earned)}</strong>
                          </td>
                          <td className="cr-num">
                            {formatAmount(s.total_commission_generated_for_uplines)}
                            <div className="cr-muted">
                              Stake {formatAmount(s.staking_commission_generated_for_uplines)} · Trade{" "}
                              {formatAmount(s.brokerage_commission_generated_for_uplines)}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cr-btn-view"
                              onClick={() => navigate(`/dashboard/commission-report/${row._id}`)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                pagination={pagination}
                loading={loading}
                onPageChange={(p) => fetchRows(p)}
                onLimitChange={(n) => fetchRows(1, n)}
              />
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default CommissionReportPage;
